import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { getDashboardGiftPacing } from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftPacingCohortRow,
  GiftPacingCurvePoint,
  GiftPacingResponse,
  GiftPacingWindowSummary,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import { CenterLoading, ErrorBox } from '../../components/DashboardShared';
import { fmt, fmtWonCompact, toKstDayKey } from './analysisUtils';

const isNotFound = (e: unknown) =>
  (e as { response?: { status?: number } })?.response?.status === 404;

/** 페이스 색 — 기대의 80% 미만은 경고, 110% 초과는 호조 */
export const paceTone = (ratio: number | null) => {
  if (ratio === null) return { text: 'text-gray-400', bg: 'bg-gray-100' };
  if (ratio < 0.8) return { text: 'text-red-600', bg: 'bg-red-50' };
  if (ratio > 1.1) return { text: 'text-emerald-600', bg: 'bg-emerald-50' };
  return { text: 'text-gray-700', bg: 'bg-gray-50' };
};

const pct = (v: number | null, digits = 0) =>
  v === null ? '—' : `${(v * 100).toFixed(digits)}%`;

/**
 * 판매 페이스 조회 상태. 페이스 뷰와 달력 뷰가 같은 응답을 쓰는데 둘이 동시에
 * 떠 있는 일이 없어(뷰 전환) 각자 부르게 두고 로직만 공유한다.
 */
export const useGiftPacing = (brandId: string, teamType: string) => {
  const [data, setData] = useState<GiftPacingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotReady(false);
    try {
      setData(await getDashboardGiftPacing(brandId || undefined, teamType || undefined));
    } catch (e) {
      if (isNotFound(e)) setNotReady(true);
      else setError(e instanceof Error ? e.message : '판매 페이스를 불러올 수 없습니다.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [brandId, teamType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, notReady, refetch: fetchData };
};

/**
 * 답례품 판매 페이스 — "지금쯤 몇 건 팔렸어야 하나".
 *
 * 답례품은 발인 후 30일까지 팔린다. 그래서 이번 달 실적을 지난달과 그냥 비교하면
 * 항상 미달로 보인다 — 과거 완료 코호트에서 배운 "발인 후 경과일별 누적 곡선"으로
 * 진행도를 보정한 뒤에야 "정말 덜 팔린 것"인지 판단할 수 있다.
 */
export const GiftConversionPacing: FC<{
  brandId: string;
  teamType: string;
}> = function GiftConversionPacing({ brandId, teamType }) {
  const {
    data,
    loading,
    error,
    notReady,
    refetch: fetchData,
  } = useGiftPacing(brandId, teamType);

  if (loading && !data) return <CenterLoading />;
  if (notReady) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-800">
        답례품 판매 페이스 API가 아직 이 서버에 배포되지 않았습니다.
      </div>
    );
  }
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!data) return null;

  const { baseline, windows, cohorts } = data;

  if (!baseline) {
    return (
      <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow">
        기준 표본이 부족합니다 — 최근 1년 완료 코호트(발인 +45일 경과)의 공유 상가가 30곳
        미만이면 기대치를 계산하지 않습니다.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── 헤드라인: 최근 발인분의 기대 대비 실적 ── */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-sm font-semibold text-gray-800">
          지금 페이스 — 최근 발인분은 지금쯤 몇 건 팔렸어야 하나
        </h3>
        <p className="mt-0.5 mb-4 text-xs text-gray-400">
          기준: 최근 1년 완료 코호트의 공유 상가당 {baseline.ordersPerSharedFevent}건 ·
          구매 전환 {pct(baseline.purchaseRateShared, 1)} · 발인 후 경과일 곡선으로
          진행도를 보정한 값과 비교한다 (발인 직후 코호트는 아직 덜 팔린 게 정상이라 그냥
          비교하면 항상 미달로 보인다)
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {windows.map((w) => (
            <WindowCard key={w.days} w={w} />
          ))}
        </div>
      </div>

      {/* ── 성숙 곡선 ── */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-sm font-semibold text-gray-800">
          발인 후 경과일별 누적 판매 곡선
        </h3>
        <p className="mt-0.5 mb-4 text-xs text-gray-400">
          기준 구간 {toKstDayKey(baseline.from)} ~ {toKstDayKey(baseline.to)} · 공유 상가{' '}
          {fmt(baseline.sharedFeventCount)}곳 / 주문 {fmt(baseline.orderCount)}건 — 실선은
          주문 건수, 점선은 상가의 첫 구매 (구매 전환이 언제 결정되는지)
        </p>
        <CurveChart
          orderCurve={baseline.orderCurve}
          firstCurve={baseline.firstPurchaseCurve}
        />
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs md:grid-cols-4">
          {[0, 3, 7, 14].map((d) => (
            <div key={d} className="rounded-lg bg-gray-50 px-3 py-2">
              <p className="text-gray-500">발인 +{d}일</p>
              <p className="mt-0.5 font-semibold text-gray-800">
                주문 {pct(cumAt(baseline.orderCurve, d), 0)} · 첫 구매{' '}
                {pct(cumAt(baseline.firstPurchaseCurve, d), 0)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── 일별 코호트 ── */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-sm font-semibold text-gray-800">
          최근 발인일별 기대 대비 실적
        </h3>
        <p className="mt-0.5 mb-4 text-xs text-gray-400">
          발인일 하루가 막대 하나 — 옅은 막대가 진행도 보정 기대치, 진한 막대가 실제 주문
          건수
        </p>
        <CohortChart cohorts={cohorts} />
      </div>
    </div>
  );
};

// ─── 내부 컴포넌트 ───────────────────────────────────

const WindowCard: FC<{ w: GiftPacingWindowSummary }> = ({ w }) => {
  const tone = paceTone(w.paceRatio);
  return (
    <div className={`rounded-lg border border-gray-100 p-4 ${tone.bg}`}>
      <p className="text-xs font-medium text-gray-500">
        최근 {w.days}일 발인 {fmt(w.feventCount)}곳
        <span className="ml-1 text-gray-400">(공유 {fmt(w.sharedFeventCount)})</span>
      </p>
      <p className={`mt-1 text-2xl font-bold ${tone.text}`}>
        {w.paceRatio === null ? '—' : `${Math.round(w.paceRatio * 100)}%`}
      </p>
      <div className="mt-1 space-y-0.5 text-xs text-gray-500">
        <p>
          주문 지금쯤{' '}
          <span className="font-semibold text-gray-700">
            {w.expectedSoFarOrderCount ?? '—'}건
          </span>{' '}
          → 실제{' '}
          <span className="font-semibold text-gray-800">{fmt(w.orderCount)}건</span>
        </p>
        <p>
          구매 상가 {w.expectedSoFarPurchasedFeventCount ?? '—'}곳 → 실제{' '}
          {fmt(w.purchasedFeventCount)}곳
          {w.paceRatioPurchasedFevent !== null &&
            ` (${Math.round(w.paceRatioPurchasedFevent * 100)}%)`}
        </p>
        <p>
          매출{' '}
          {w.expectedSoFarAmount !== null ? fmtWonCompact(w.expectedSoFarAmount) : '—'}원
          → 실제 {fmtWonCompact(w.amount)}원
          {w.paceRatioAmount !== null && ` (${Math.round(w.paceRatioAmount * 100)}%)`}
        </p>
        {w.expectedFinalOrderCount !== null && (
          <p className="text-gray-400">다 익으면 {w.expectedFinalOrderCount}건 예상</p>
        )}
      </div>
    </div>
  );
};

/** 곡선에서 경과일 d의 누적 비중 (선형 보간) */
const cumAt = (curve: GiftPacingCurvePoint[], day: number): number | null => {
  const first = curve[0];
  const last = curve[curve.length - 1];
  if (!first || !last) return null;
  if (day <= first.day) return first.cumShare;
  if (day >= last.day) return last.cumShare;
  for (let i = 1; i < curve.length; i++) {
    const prev = curve[i - 1];
    const cur = curve[i];
    if (!prev || !cur) continue;
    if (day <= cur.day) {
      const span = cur.day - prev.day;
      const ratio = span === 0 ? 0 : (day - prev.day) / span;
      return prev.cumShare + (cur.cumShare - prev.cumShare) * ratio;
    }
  }
  return last.cumShare;
};

const CHART_W = 1000;
const CHART_H = 220;
const PAD_L = 44;
const PAD_B = 24;

/** 누적 곡선 2개 (주문 / 첫 구매) — 가로축 발인 후 경과일 */
const CurveChart: FC<{
  orderCurve: GiftPacingCurvePoint[];
  firstCurve: GiftPacingCurvePoint[];
}> = ({ orderCurve, firstCurve }) => {
  const minDay = orderCurve[0]?.day ?? -7;
  const maxDay = orderCurve[orderCurve.length - 1]?.day ?? 45;
  const span = maxDay - minDay || 1;

  const x = (day: number) => PAD_L + ((day - minDay) / span) * (CHART_W - PAD_L - 8);
  const y = (share: number) => (1 - share) * (CHART_H - PAD_B);

  const toPath = (curve: GiftPacingCurvePoint[]) =>
    curve.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.day)},${y(p.cumShare)}`).join(' ');

  const ticks = [-7, 0, 7, 14, 21, 28, 35, 45].filter((d) => d >= minDay && d <= maxDay);

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className="h-56 w-full min-w-[560px]"
        preserveAspectRatio="none"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((g) => (
          <g key={g}>
            <line x1={PAD_L} x2={CHART_W - 8} y1={y(g)} y2={y(g)} stroke="#f1f5f9" />
            <text x={0} y={y(g) + 4} fontSize={11} fill="#94a3b8">
              {g * 100}%
            </text>
          </g>
        ))}
        {/* 발인 시점 */}
        <line
          x1={x(0)}
          x2={x(0)}
          y1={0}
          y2={CHART_H - PAD_B}
          stroke="#cbd5e1"
          strokeDasharray="3 3"
        />
        <path d={toPath(orderCurve)} fill="none" stroke="#6366f1" strokeWidth={2.5} />
        <path
          d={toPath(firstCurve)}
          fill="none"
          stroke="#10b981"
          strokeWidth={2}
          strokeDasharray="5 4"
        />
        {ticks.map((d) => (
          <text
            key={d}
            x={x(d)}
            y={CHART_H - 6}
            fontSize={11}
            fill="#94a3b8"
            textAnchor="middle"
          >
            {d === 0 ? '발인' : `${d > 0 ? '+' : ''}${d}일`}
          </text>
        ))}
      </svg>
      <div className="mt-1 flex gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-4 bg-indigo-500" /> 주문 건수 누적
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-4 bg-emerald-500" /> 상가 첫 구매 누적
        </span>
      </div>
    </div>
  );
};

/** 일별 코호트 — 기대(옅은) vs 실제(진한) 주문 건수 */
const CohortChart: FC<{ cohorts: GiftPacingCohortRow[] }> = ({ cohorts }) => {
  const max = useMemo(
    () =>
      Math.max(
        1,
        ...cohorts.map((c) => Math.max(c.orderCount, c.expectedSoFarOrderCount ?? 0)),
      ),
    [cohorts],
  );

  return (
    <div className="overflow-x-auto">
      <div className="flex min-w-[560px] items-end gap-[3px]">
        {cohorts.map((c) => {
          const expected = c.expectedSoFarOrderCount ?? 0;
          const tone = paceTone(c.paceRatio);
          return (
            <div
              key={c.date}
              className="group relative flex h-40 flex-1 items-end justify-center"
            >
              <div className="pointer-events-none absolute -top-1 left-1/2 z-10 hidden -translate-x-1/2 -translate-y-full whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight text-white shadow group-hover:block">
                {c.date.slice(5)} 발인 (D+{c.elapsedDays}) · 상가 {fmt(c.feventCount)}
                곳(공유 {fmt(c.sharedFeventCount)}) · 진행 {pct(c.expectedProgress)} —
                지금쯤 {expected}건, 실제 {fmt(c.orderCount)}건
                {c.paceRatio !== null && ` (${Math.round(c.paceRatio * 100)}%)`}
                {c.projectedFinalOrderCount !== null &&
                  ` · 최종 예상 ${c.projectedFinalOrderCount}건`}
              </div>
              {/* 기대 막대 (뒤) */}
              <div
                className="w-full rounded-t bg-gray-200"
                style={{ height: `${(expected / max) * 100}%` }}
              />
              {/* 실제 막대 (앞, 겹쳐서) */}
              <div
                className={`absolute bottom-0 w-1/2 rounded-t ${
                  c.paceRatio !== null && c.paceRatio < 0.8
                    ? 'bg-red-500'
                    : 'bg-indigo-500'
                } ${tone.text}`}
                style={{ height: `${(c.orderCount / max) * 100}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex min-w-[560px] gap-[3px]">
        {cohorts.map((c, i) => (
          <span
            key={c.date}
            className="flex-1 text-center text-[9px] leading-none text-gray-400"
          >
            {i % 5 === 0 ? c.date.slice(5) : ''}
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-gray-200" /> 기대
          누적(진행도 보정)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-500" /> 실제 주문
        </span>
      </div>
    </div>
  );
};
